import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Central launcher state: server-side permission-filtered tool access,
// per-account favourites and recently-used. Optimistic UI, persisted server-side.
export const useLauncher = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [allowed, setAllowed] = useState(null); // null = loading
  const [favourites, setFavourites] = useState([]);
  const [recent, setRecent] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [toolOrder, setToolOrder] = useState({});
  const [layout, setLayout] = useState('refined');

  const authHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token],
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/home/launcher`, { headers: authHeaders() });
        if (!res.ok) throw new Error('launcher');
        const data = await res.json();
        if (cancelled) return;
        setAllowed(new Set(data.allowed || []));
        setFavourites(data.favourites || []);
        setRecent(data.recent || []);
        setGroupOrder(data.sidebar_group_order || []);
        setToolOrder(data.sidebar_tool_order || {});
        setLayout(data.sidebar_layout || 'refined');
      } catch (_) {
        if (!cancelled) setAllowed(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [token, authHeaders]);

  const isFav = useCallback((key) => favourites.includes(key), [favourites]);

  const toggleFavourite = useCallback((key) => {
    setFavourites((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      fetch(`${backendUrl}/api/home/favourites`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ tool_keys: next }),
      }).catch(() => {});
      return next;
    });
  }, [authHeaders]);

  // Persist a fully-ordered favourites list (used by the dashboard customiser).
  const saveFavourites = useCallback((keys) => {
    const next = Array.from(new Set(keys));
    setFavourites(next);
    fetch(`${backendUrl}/api/home/favourites`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ tool_keys: next }),
    }).catch(() => {});
  }, [authHeaders]);

  const recordRecent = useCallback((key) => {
    setRecent((prev) => [key, ...prev.filter((k) => k !== key)].slice(0, 8));
    fetch(`${backendUrl}/api/home/recent`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ tool_key: key }),
    }).catch(() => {});
  }, [authHeaders]);

  const launch = useCallback((tool) => {
    recordRecent(tool.key);
    navigate(tool.path);
  }, [recordRecent, navigate]);

  const saveSidebarOrder = useCallback((nextGroupOrder, nextToolOrder) => {
    setGroupOrder(nextGroupOrder);
    setToolOrder(nextToolOrder);
    fetch(`${backendUrl}/api/home/sidebar-order`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ group_order: nextGroupOrder, tool_order: nextToolOrder }),
    }).catch(() => {});
  }, [authHeaders]);

  const saveLayout = useCallback((nextLayout) => {
    setLayout(nextLayout);
    fetch(`${backendUrl}/api/home/sidebar-layout`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ layout: nextLayout }),
    }).catch(() => {});
  }, [authHeaders]);

  return {
    loading: allowed === null,
    allowed: allowed || new Set(),
    favourites, recent,
    groupOrder, toolOrder, saveSidebarOrder,
    layout, saveLayout,
    isFav, toggleFavourite, saveFavourites, recordRecent, launch,
  };
};
